import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { cn } from "@utils/tw"
import { defineBlock, EditableText, useInlineEditableValue } from "eddev/blocks"
import { useEffect, useRef, useState } from "react"
import { motion } from "motion/react"
import { Button } from "@components/atoms/Button"
import { Appear } from "@components/motion/Appear"
import { ResponsiveImageFragment } from "../../types.graphql"
import { themes } from "@components/atoms/Themed"

export const meta: BlockMeta = {
  title: "Features Grid",
  tags: ["#page"],
}

export default defineBlock("content/features-grid", (props) => {
  const features = props.features || []
  const [selected, setSelected] = useState(0)
  const [title, setTitle] = useInlineEditableValue("title", "")
  const showTitle = env.admin || (title && title.length > 0)

  return (
    <div className="content-sized my-blockSpace flex flex-col gap-7 md:gap-6">
      {showTitle && (
        <Appear slide>
          <EditableText store="title" className="type-sans-2-xl-semibold" placeholder="Features Grid Title" />
        </Appear>
      )}
      {env.admin && features.length === 0 && (
        <div className="w-full col-span-2 p-5 rounded-md bg-slate200 text-slate950 type-sans-base-regular">
          No features added. Please add features to display the features grid.
        </div>
      )}
      <div className="flex flex-col gap-5 md:gap-4">
        {features.map((row, rowIndex) => {
          const items = row?.rowItems || []
          return (
            !!row && (
              <div
                key={rowIndex}
                className={cn(
                  "w-full grid gap-5 md:gap-4 grid-cols-1",
                  items.length === 3 && "md:grid-cols-3",
                  items.length === 2 && "md:grid-cols-2",
                  items.length === 1 && "md:grid-cols-1",
                )}
              >
                {items.map((feature, colIndex) => {
                  const theme = feature?.theme || "slateLight"
                  const bg = themes[theme as keyof typeof themes].bg
                  const text = themes[theme as keyof typeof themes].text
                  const delay = rowIndex * 150 + colIndex * 150

                  return (
                    !!feature && (
                      <GridItem
                        key={rowIndex * items.length + colIndex}
                        total={items.length}
                        bg={bg}
                        text={text}
                        delay={delay}
                        backgroundImage={feature.backgroundImage}
                        subtitle={feature.subtitle}
                        title={feature.title}
                        activeText={feature.activeText}
                      />
                    )
                  )
                })}
              </div>
            )
          )
        })}
      </div>
    </div>
  )
})

const GridItem = ({
  total,
  bg,
  text,
  delay,
  backgroundImage,
  subtitle,
  title,
  activeText,
}: {
  total: number
  bg: string
  text: string
  delay: number
  backgroundImage?: ResponsiveImageFragment | null
  subtitle: string | null
  title: string | null
  activeText?: string | null
}) => {
  const [open, setOpen] = useState(false)

  return (
    <Appear
      delay={delay}
      className={cn(
        "w-full flex flex-col justify-between rounded-md p-5 md:p-4 relative overflow-hidden aspect-[352/256] group",
        total === 3 && "md:aspect-[342/256]",
        total === 2 && "md:aspect-[520/160]",
        total === 1 && "md:aspect-[1057/160]",
        bg,
        text,
      )}
      onClick={() => !!activeText && setOpen(!open)}
    >
      {backgroundImage && (
        <Appear delay={delay} zoom duration={1000} className="w-full h-full absolute left-0 top-0">
          <ResponsiveImage {...backgroundImage} className="w-full h-full absolute left-0 top-0" objectFit="cover" />
          <div
            className="w-full h-full absolute left-0 top-0"
            style={{
              background:
                "background: linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.01) 6.67%, rgba(1, 1, 1, 0.04) 13.33%, rgba(2, 2, 2, 0.08) 20%, rgba(4, 4, 4, 0.15) 26.67%, rgba(6, 6, 6, 0.23) 33.33%, rgba(9, 9, 9, 0.33) 40%, rgba(12, 12, 12, 0.44) 46.67%, rgba(14, 14, 14, 0.56) 53.33%, rgba(17, 17, 17, 0.67) 60%, rgba(20, 20, 20, 0.77) 66.67%, rgba(22, 22, 22, 0.85) 73.33%, rgba(24, 24, 24, 0.92) 80%, rgba(25, 25, 25, 0.96) 86.67%, rgba(26, 26, 26, 0.99) 93.33%, #1A1A1A 100%)",
            }}
          />
        </Appear>
      )}
      <Appear as="p" delay={delay} className="type-sans-sm-semibold text-pretty relative">
        {subtitle}
      </Appear>
      <Appear as="p" slide delay={delay + 50} className="type-serif-xl-regular text-pretty relative pr-9 md:pr-0">
        {title}
      </Appear>

      {activeText && (
        <>
          <div
            className={cn(
              "absolute left-0 top-0 w-full h-full pointer-events-none transition-opacity duration-300 bg-slate600 p-5 md:p-4 type-serif-base-regular whitespace-pre-line text-white md:group-hover:opacity-100 md:group-hover:pointer-events-auto opacity-0",
              open && "opacity-100 pointer-events-auto",
            )}
          >
            {activeText}
          </div>

          <svg
            className={cn("w-8 h-8 absolute right-7 bottom-7 md:hidden", open && "hidden")}
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="16" cy="16" r="16" fill="#E2E8F0" />
            <path
              d="M15.3077 16.6923H10V15.3077H15.3077V10H16.6923V15.3077H22V16.6923H16.6923V22H15.3077V16.6923Z"
              fill="#90A1B9"
            />
          </svg>

          <svg
            className={cn("w-8 h-8 absolute right-7 bottom-7 md:hidden", !open && "hidden")}
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="16" cy="16" r="16" fill="#E2E8F0" />
            <path
              d="M15.3077 16.6924H10V15.3077H15.3077L16.6923 15.3078L22 15.3077V16.6924H16.6923H15.3077Z"
              fill="#90A1B9"
            />
          </svg>
        </>
      )}
    </Appear>
  )
}
